import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { Table, Alert, Popconfirm } from "antd"
import { Link } from "react-router-dom"

import { formatCurrency } from "../../../lib/utilities"
import {
    discardRun,
    selectExceptions,
    selectRun,
    selectSwept,
    selectSweepMessage,
    selectTotals,
} from "../clientBatchingSlice"

export default function SweepResult() {
    const dispatch = useDispatch()
    const run = useSelector(selectRun)
    const swept = useSelector(selectSwept)
    const exceptions = useSelector(selectExceptions)
    const totals = useSelector(selectTotals)
    const sweepMessage = useSelector(selectSweepMessage)

    // Nothing to show until a sweep has run.
    if (!run && swept.length === 0 && exceptions.length === 0) {
        return sweepMessage ? <Alert type="info" showIcon message={sweepMessage} /> : null
    }

    return (
        <div className="muc-table card" style={{ marginTop: "20px" }}>
            <div className="card-body">
                {/* single wrapper: .card-body is a flex row, see ClientPickerTable */}
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>Swept</h2>
                            {totals && <div className="badge">{totals.bill_count}</div>}
                        </div>

                        {run && (
                            <div className="actions">
                                <div className="table-action-links">
                                    <Link to={`/client_batching/runs/${run.id}`}>Review bills</Link>

                                    <Popconfirm
                                    title="Discard this run? The clients it holds are released and no bill is changed."
                                    okText="Discard"
                                    cancelText="Keep"
                                    onConfirm={() => dispatch(discardRun(run.id))}
                                >
                                    <a href="javascript:;">Discard run</a>
                                    </Popconfirm>
                                </div>
                            </div>
                        )}
                    </div>

                    {run && (
                        <p className="p-10">
                            Run #{run.id} · {run.batch_date} ·{" "}
                            {totals && `${totals.client_count} client(s), ${totals.batch_group_count} batch(es), `}
                            {totals && `${formatCurrency(totals.current_charges_sum)} current charges`}
                        </p>
                    )}

                    {totals && totals.flagged_bill_count > 0 && run && (
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: "10px" }}
                            message={`${totals.flagged_bill_count} red-flagged bill(s) held out of this run`}
                            description={
                                <span>
                                    Their current charges do not add up. They are listed on the{" "}
                                    <Link to={`/client_batching/runs/${run.id}/flagged`}>Flagged tab</Link> and move into
                                    the run once fixed.
                                </span>
                            }
                        />
                    )}

                    {exceptions.map((exception) => (
                        <Alert
                            key={`${exception.client_id}-${exception.division_id}`}
                            type="warning"
                            showIcon
                            style={{ marginBottom: "10px" }}
                            message={`${exception.client_name}: ${exception.message}`}
                        />
                    ))}

                    <Table dataSource={swept} rowKey={(r) => `${r.client_id}-${r.batch_group_id || "none"}`} pagination={false}>
                        <Table.Column title="Client" key="client_name" dataIndex="client_name" />
                        <Table.Column
                            title="Batch group"
                            key="batch_group_name"
                            render={(text, record) => record.batch_group_name || "No batch group"}
                        />
                        <Table.Column title="Bills" key="bill_count" dataIndex="bill_count" />
                        <Table.Column
                            title="Current charges"
                            key="current_charges_sum"
                            render={(text, record) => formatCurrency(record.current_charges_sum)}
                        />
                    </Table>
                </div>
            </div>
        </div>
    )
}
